import { mapSubscription } from "@components/PositionsPayRangesPage/mapPositionSubscriptionRow";

const WORK_MODE_LABEL = {
  ON_SITE: "On-Site",
  HYBRID: "Hybrid",
  REMOTE: "Remote",
  UNSPECIFIED: "Unspecified",
};

const CURRENT_PLAN_VARIANTS = new Set([
  "current",
  "current-future",
  "past-current",
  "past-current-future",
]);
const FAIR_PAY_TOLERANCE_PCT = 5;

function toNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function unwrapList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function isOpenAsOf(start, end, asOf = todayIso()) {
  if (start && start > asOf) return false;
  if (end && end < asOf) return false;
  return true;
}

function ladderFromMinMax(min, max) {
  const lo = toNumber(min) ?? 0;
  const hi = toNumber(max) ?? 0;
  const span = Math.max(hi - lo, 0);
  return [lo, lo + span / 3, lo + (span * 2) / 3, hi];
}

function ladderFromReadiness(readiness) {
  if (!readiness) return null;
  const values = [readiness.min, readiness.at50, readiness.at100, readiness.max]
    .map(toNumber)
    .filter((n) => n != null);
  return values.length === 4 ? values : null;
}

export function hasCurrentPayPlanDetail(row) {
  const variant = row?.currentPayPlan?.variant;
  return Boolean(row?.payPlan) && CURRENT_PLAN_VARIANTS.has(variant);
}

export function buildFairPayDelta(baseSalary, fairPayToday) {
  const salary = toNumber(baseSalary) ?? 0;
  const fair = toNumber(fairPayToday) ?? 0;
  const amount = Math.abs(salary - fair);
  const pct = fair ? Number((((salary - fair) / fair) * 100).toFixed(1)) : 0;
  const direction = salary < fair ? "below" : salary > fair ? "above" : "at";
  return {
    amount,
    pct,
    direction,
    withinTolerance: Math.abs(pct) <= FAIR_PAY_TOLERANCE_PCT,
  };
}

export function pickCurrentSubscription(payload) {
  const rows = unwrapList(payload)
    .map((item) => mapSubscription(item) || item)
    .filter(Boolean);
  const open = rows.filter((item) =>
    isOpenAsOf(item.effectiveFrom || item.effective_from, item.effectiveTo || item.effective_to)
  );
  return open[0] || rows[0] || null;
}

function emptyPayRange(currencyCode = "USD") {
  return {
    id: null,
    code: "",
    name: "",
    country: "",
    countryCode: "",
    workMode: "",
    currencyUnit: `${currencyCode}/year`,
    values: [0, 0, 0, 0],
    version: "",
    asOf: "",
  };
}

function mapPayRange(subscription, payRangeApi, currencyCode) {
  const snap = payRangeApi?.compensation_snapshot || {};
  const country = payRangeApi?.operating_country || {};
  const region = payRangeApi?.operating_region || "";
  const mappedSub = subscription?.payRangeId ? subscription : mapSubscription(subscription);
  const min = mappedSub?.minAmount ?? snap.min_amount;
  const max = mappedSub?.maxAmount ?? snap.max_amount;
  const values =
    ladderFromReadiness(mappedSub?.readiness) || ladderFromMinMax(min, max);

  const countryName = country.name || country.code || "";
  const countryLine = region
    ? `${countryName} - ${region}`
    : countryName
      ? `${countryName} - Country Wide`
      : "";

  const versionNumber = snap.version_number;
  return {
    id: payRangeApi?.id || mappedSub?.payRangeId || null,
    code: mappedSub?.payRangeCode || payRangeApi?.code || "",
    name: mappedSub?.payRangeName || payRangeApi?.name || "",
    country: countryLine,
    countryCode: String(country.code || "").toLowerCase(),
    workMode: WORK_MODE_LABEL[payRangeApi?.work_mode] || payRangeApi?.work_mode || "",
    currencyUnit: `${currencyCode}/year`,
    values,
    version: versionNumber ? `V${versionNumber}` : "",
    asOf: snap.effective_from || mappedSub?.effectiveFrom || "",
  };
}

/**
 * Map a Pay Plans list row (+ optional subscription / pay-range detail)
 * onto CurrentPayPlanCard props.
 */
export function mapCurrentPayPlanCard({ row, subscription = null, payRange = null } = {}) {
  const plan = row?.payPlan || {};
  const currency = plan.currency_code || row?.fairPayStatus?.currencyCode || "USD";
  const baseSalary = toNumber(plan.amount) ?? 0;
  const mappedRange = payRange || subscription ? mapPayRange(subscription, payRange, currency) : emptyPayRange(currency);
  const fairPayToday = toNumber(mappedRange.values?.[2]) ?? baseSalary;
  const versionNumber = plan.version_number;

  return {
    payPlanCode: plan.code || row?.currentPayPlan?.planReference || "",
    effectiveDate: plan.effective_on || "",
    version: versionNumber ? `V${versionNumber}` : "",
    baseSalary,
    currency,
    fairPayToday,
    fairPayDelta: buildFairPayDelta(baseSalary, fairPayToday),
    readinessPct: Math.min(150, Math.max(0, Number(row?.fairPayStatus?.readinessPct) || 0)),
    readinessAsOf: "today",
    primaryPosition: {
      code: row?.primaryPosition?.code || "",
      name: row?.primaryPosition?.name || row?.employee?.positionTitle || "",
    },
    payRange: mappedRange,
    fromProposal: false,
    proposal: null,
  };
}
